/* global Word, Office, crypto */

const NAME_PREFIX = "Cenna_";
const SEARCH_MAX = 200;

export type AnchorDescriptor = {
  type: "bookmark";
  id: string;
  versionId: string;
  quoteText?: string;
  normalizedQuote?: string;
  quoteHash?: string;
  paragraphIndex?: number;
  resolvedVersions?: Record<string, string>;
};

export type AnchorResolveResult = "exact" | "search" | "approx" | "notfound";

export function isAnchorSupported(): boolean {
  try {
    return Office.context.requirements.isSetSupported("WordApi", "1.4");
  } catch {
    return false;
  }
}

function newBookmarkName(): string {
  return NAME_PREFIX + crypto.randomUUID().replace(/-/g, "");
}

function normalizeQuote(text: string): string {
  return text.replace(/\s+/g, " ").trim().toLowerCase();
}

async function sha256Hex(value: string): Promise<string> {
  const data = new TextEncoder().encode(value);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

async function findRangeForQuote(
  context: Word.RequestContext,
  quoteText: string,
): Promise<{ range: Word.Range; matchCount: number } | null> {
  const needle = quoteText.trim().slice(0, SEARCH_MAX);
  if (!needle) {
    return null;
  }
  const found = context.document.body.search(needle, { matchCase: false });
  found.load("items");
  await context.sync();
  if (found.items.length < 1) {
    return null;
  }
  return { range: found.items[0], matchCount: found.items.length };
}

async function resolveRangeForQuote(
  context: Word.RequestContext,
  quoteText: string,
): Promise<{ range: Word.Range; matchCount: number } | null> {
  const trimmed = quoteText.trim();
  if (!trimmed) {
    return null;
  }

  const selection = context.document.getSelection();
  selection.load("text");
  await context.sync();
  const selectionText = (selection.text ?? "").trim();
  if (selectionText && normalizeQuote(selectionText) === normalizeQuote(trimmed)) {
    return { range: selection, matchCount: 1 };
  }

  return findRangeForQuote(context, trimmed);
}

/**
 * Insert a bookmark on the quoted selection so the add-in can scroll back to it
 * later (exact) and fall back to a text search across copies/versions.
 */
export async function captureSelectionAnchor(
  versionId: string,
  quoteText: string,
): Promise<AnchorDescriptor | null> {
  const trimmed = quoteText.trim();
  if (!trimmed || !isAnchorSupported()) {
    return null;
  }

  return Word.run(async (context) => {
    const located = await resolveRangeForQuote(context, trimmed);
    if (!located) {
      return null;
    }

    const bookmarkName = newBookmarkName();
    located.range.insertBookmark(bookmarkName);
    await context.sync();

    const normalizedQuote = normalizeQuote(trimmed);
    return {
      type: "bookmark",
      id: bookmarkName,
      versionId,
      quoteText: trimmed,
      normalizedQuote,
      quoteHash: await sha256Hex(normalizedQuote),
    };
  });
}

export type FocusAnchorSelfHeal = {
  anchor: AnchorDescriptor;
  resolve: AnchorResolveResult;
};

/** Resolve an anchor in the currently open document and scroll to it. */
export async function focusAnchor(
  anchor: AnchorDescriptor,
  currentVersionId: string,
): Promise<FocusAnchorSelfHeal> {
  if (!isAnchorSupported()) {
    return { anchor, resolve: "notfound" };
  }

  return Word.run(async (context) => {
    const name = anchor.resolvedVersions?.[currentVersionId] ?? anchor.id;
    if (name) {
      const bookmarkRange = context.document.getBookmarkRangeOrNullObject(name);
      bookmarkRange.load("isNullObject");
      await context.sync();
      if (!bookmarkRange.isNullObject) {
        bookmarkRange.select();
        await context.sync();
        return { anchor, resolve: "exact" };
      }
    }

    const quoteText = anchor.quoteText?.trim();
    if (!quoteText) {
      return { anchor, resolve: "notfound" };
    }

    const located = await resolveRangeForQuote(context, quoteText);
    if (!located) {
      return { anchor, resolve: "notfound" };
    }

    located.range.select();
    const newName = newBookmarkName();
    located.range.insertBookmark(newName);
    await context.sync();

    const healed: AnchorDescriptor = {
      ...anchor,
      id: newName,
      versionId: currentVersionId,
      resolvedVersions: {
        ...(anchor.resolvedVersions ?? {}),
        [currentVersionId]: newName,
      },
    };
    return {
      anchor: healed,
      resolve: located.matchCount === 1 ? "search" : "approx",
    };
  });
}
